/**
 * Formats a duration in seconds into MM:SS format.
 * If already in MM:SS format, returns it directly.
 * Returns 'N/A' if invalid or null/undefined.
 */
export const formatSecondsToTime = (seconds) => {
    if (seconds === null || seconds === undefined || seconds === 'N/A' || seconds === '') return 'N/A';
    if (typeof seconds === 'string' && /^\d+:\d{2}$/.test(seconds.trim())) {
        return seconds.trim();
    }
    const totalSeconds = Number(seconds);
    if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return 'N/A';
    const mins = Math.floor(totalSeconds / 60);
    const secs = Math.floor(totalSeconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

/**
 * Resolves the time taken in seconds for a specific assessment result.
 * Checks in priority order:
 * 1. assessment.timeTaken (explicitly stored on the assessment record)
 * 2. LocalStorage (recently submitted from this browser)
 * 3. Candidate comment text matching this quiz title or general Time taken
 * 4. AttemptedAt timestamps (diff between this attempt and previous attempt / application date)
 * 5. Sensible fallback based on questions count if completed with score
 */
export const getAssessmentTimeTaken = (assessment, index = 0, allAssessments = [], candidate = null) => {
    if (!assessment) return null;

    const quizDurationMinutes = assessment.quizId?.duration || 30;
    const maxAllowedSeconds = quizDurationMinutes * 60;

    // 1. Explicitly stored timeTaken
    if (assessment.timeTaken !== undefined && assessment.timeTaken !== null && assessment.timeTaken !== '') {
        const val = Number(assessment.timeTaken);
        if (Number.isFinite(val) && val >= 0) {
            return Math.min(Math.round(val), maxAllowedSeconds);
        }
    }

    // 2. LocalStorage fallback
    if (typeof window !== 'undefined' && window.localStorage) {
        try {
            const candId = candidate?._id || assessment.candidateId;
            const qId = assessment.quizId?._id || (typeof assessment.quizId === 'string' ? assessment.quizId : null);
            const qTitle = assessment.quizTitle || assessment.quizId?.title;
            if (candId) {
                const key1 = qId ? `assessment_time_${candId}_${qId}` : null;
                const key2 = qTitle ? `assessment_time_${candId}_${qTitle}` : null;
                const stored = (key1 && localStorage.getItem(key1)) || (key2 && localStorage.getItem(key2));
                if (stored) {
                    const val = Number(stored);
                    if (Number.isFinite(val) && val >= 0) {
                        return Math.min(Math.round(val), maxAllowedSeconds);
                    }
                }
            }
        } catch (e) {}
    }

    // 3. Comment matching
    const comment = candidate?.comment || assessment.comment;
    if (comment && typeof comment === 'string') {
        const qTitle = assessment.quizTitle || (typeof assessment.quizId === 'object' ? assessment.quizId?.title : null);
        if (qTitle) {
            const escapedTitle = qTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const pattern = new RegExp(`"${escapedTitle}"[^|\\n]*?Time taken:\\s*(\\d+):(\\d{2})`, 'i');
            const match = comment.match(pattern);
            if (match) {
                const sec = (Number(match[1]) * 60) + Number(match[2]);
                return Math.min(sec, maxAllowedSeconds);
            }
        }
        // If single assessment or index 0
        const allMatches = [...comment.matchAll(/Time taken:\s*(\d+):(\d{2})/gi)];
        if (allMatches.length === 1) {
            return (Number(allMatches[0][1]) * 60) + Number(allMatches[0][2]);
        } else if (allMatches.length > index && allMatches[index]) {
            return (Number(allMatches[index][1]) * 60) + Number(allMatches[index][2]);
        }
    }

    // 4. Timestamp difference
    const currentAttempted = assessment.attemptedAt ? new Date(assessment.attemptedAt).getTime() : null;
    if (currentAttempted && Number.isFinite(currentAttempted)) {
        let prevTime = null;
        if (index > 0 && allAssessments && allAssessments[index - 1]?.attemptedAt) {
            prevTime = new Date(allAssessments[index - 1].attemptedAt).getTime();
        } else if (candidate?.appliedAt || candidate?.createdAt) {
            prevTime = new Date(candidate.appliedAt || candidate.createdAt).getTime();
        }

        if (prevTime && Number.isFinite(prevTime) && currentAttempted > prevTime) {
            const diffSeconds = Math.round((currentAttempted - prevTime) / 1000);
            if (diffSeconds > 0 && diffSeconds <= (maxAllowedSeconds + 180)) {
                return Math.min(diffSeconds, maxAllowedSeconds);
            }
            if (diffSeconds > 0 && diffSeconds <= maxAllowedSeconds * 2) {
                return maxAllowedSeconds;
            }
        }
    }

    // 5. Question count fallback if test has completed score
    if (assessment.score !== undefined && assessment.score !== null) {
        const totalQuestions = assessment.totalQuestions || assessment.answers?.length || 15;
        const estimatedSeconds = Math.min(totalQuestions * 25, maxAllowedSeconds);
        return estimatedSeconds;
    }

    return null;
};

/**
 * Calculates total time taken across all assessments for a candidate.
 */
export const getCandidateTotalTimeTaken = (candidate) => {
    if (!candidate || !candidate.assessmentResults || candidate.assessmentResults.length === 0) {
        return null;
    }
    const assessments = candidate.assessmentResults;
    let total = 0;
    let hasAnyValid = false;

    for (let i = 0; i < assessments.length; i++) {
        const timeSec = getAssessmentTimeTaken(assessments[i], i, assessments, candidate);
        if (timeSec !== null && timeSec !== undefined && Number.isFinite(timeSec) && timeSec >= 0) {
            total += timeSec;
            hasAnyValid = true;
        }
    }

    return hasAnyValid ? total : null;
};